import { useEffect, useRef } from 'react';

import { TimerCycles } from '@entities/timer';
import { isTouch } from '@shared/lib';

// ---

const tickingSound = new URL(
	'@shared/assets/sounds/ticking.mp3',
	import.meta.url
);

const windingUpSound = new URL(
	'@shared/assets/sounds/winding-up.mp3',
	import.meta.url
);

const bellRingSound = new URL(
	'@shared/assets/sounds/bell-ring.mp3',
	import.meta.url
);

interface SoundPlayerProps {
	volumeLevel: number; // 0 to 1
	isMuted: boolean;
	windTime: number;
	currentCycle: TimerCycles;
	playEndSound: boolean;
}

export default function SoundPlayer({
	volumeLevel,
	isMuted,
	windTime,
	playEndSound,
	currentCycle,
}: SoundPlayerProps) {
	const isFirstRender = useRef(true);

	const tickingAudioRef = useRef<HTMLAudioElement>(null);
	const windingUpAudioRef = useRef<HTMLAudioElement>(null);
	const endRingingAudioRef = useRef<HTMLAudioElement>(null);

	// Pool a few Audio instances for overlapping winding sounds without reallocations
	const windingPoolRef = useRef<HTMLAudioElement[]>([]);
	const poolIndexRef = useRef(0);
	const POOL_SIZE = 4;

	// Track previous windTime to detect increases (winding up)
	const prevWindTimeRef = useRef<number>(windTime);

	useEffect(() => {
		const audio = tickingAudioRef.current;
		if (audio) {
			audio.muted = isMuted;
			if (currentCycle === TimerCycles.WORK && !isTouch(window)) {
				audio.currentTime = 0;
				audio.play();
			} else {
				audio.pause();
			}
		}
	}, [isMuted, currentCycle]);

	// Keep pool and other audios in sync with mute changes
	useEffect(() => {
		if (windingUpAudioRef.current) {
			windingUpAudioRef.current.muted = isMuted;
		}
		if (endRingingAudioRef.current) {
			endRingingAudioRef.current.muted = isMuted;
		}
		if (windingPoolRef.current.length) {
			windingPoolRef.current.forEach((a) => (a.muted = isMuted));
		}
	}, [isMuted]);

	// Initialize winding sound pool once
	useEffect(() => {
		if (windingPoolRef.current.length === 0) {
			const pool: HTMLAudioElement[] = [];
			for (let i = 0; i < POOL_SIZE; i++) {
				const a = new Audio(windingUpSound.href);
				a.preload = 'auto';
				a.volume = volumeLevel;
				a.muted = isMuted;
				pool.push(a);
			}
			windingPoolRef.current = pool;
		}
		// ensure base ref mirrors mute state
		if (windingUpAudioRef.current) {
			windingUpAudioRef.current.muted = isMuted;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Play guaranteed winding-up sound when minutes increase
	useEffect(() => {
		const prev = prevWindTimeRef.current;
		prevWindTimeRef.current = windTime;
		if (windTime > 0 && (windTime > prev || windTime < prev)) {
			const pool = windingPoolRef.current;
			if (pool.length) {
				const idx = poolIndexRef.current % pool.length;
				poolIndexRef.current = (poolIndexRef.current + 1) % pool.length;
				const audio = pool[idx];
				try {
					audio.currentTime = 0;
					audio.muted = isMuted;
					audio.volume = volumeLevel;
					audio.play().catch(() => {});
				} catch (_) {
					// noop
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [windTime]);

	useEffect(() => {
		const endRingingAudio = endRingingAudioRef.current;
		if (endRingingAudio) {
			endRingingAudio.volume = 1;
			endRingingAudio.muted = isMuted;
			if (playEndSound) {
				endRingingAudio.currentTime = 0;
				endRingingAudio.play();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playEndSound]);

	useEffect(() => {
		const tickingAudio = tickingAudioRef.current;
		const windingUpAudio = windingUpAudioRef.current;

		if (tickingAudio) {
			tickingAudio.volume = isTouch(window) ? 0 : volumeLevel;
		}

		if (windingUpAudio) {
			windingUpAudio.volume = volumeLevel;
		}

		// update pool volumes as well
		if (windingPoolRef.current.length) {
			windingPoolRef.current.forEach((a) => (a.volume = volumeLevel));
		}
	}, [volumeLevel]);

	isFirstRender.current = false;

	return (
		<>
			<audio ref={tickingAudioRef} loop>
				<source src={tickingSound.href} type="audio/mp3" />
			</audio>
			<audio ref={endRingingAudioRef}>
				<source src={bellRingSound.href} type="audio/mp3" />
			</audio>
			<audio ref={windingUpAudioRef} preload="auto">
				<source src={windingUpSound.href} type="audio/mp3" />
			</audio>
		</>
	);
}
